import React, { useState, useEffect } from 'react';
import HandshakeIcon from '../../../components/HandshakeIcon';
import {
  subscribe,
  getEthicalSourcingOverview,
  getEthicalSuppliers,
  getEthicalStandards,
  getSourcingPolicies,
  getCertificationPrograms,
  getRiskAssessment,
  getMonitoringKPIs,
  getSupplierById,
  getHighPerformingSuppliers,
  getSuppliersNeedingImprovement,
  assessSupplierRisk,
  calculateEthicalSourcingTrends,
  generateEthicalSourcingReport,
  refreshEthicalSourcingData,
  exportEthicalSourcingData
} from '../../../services/ethics/ethicalSourcingService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const EthicalSourcing = () => {
  const [ethicalData, setEthicalData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCriteria, setFilterCriteria] = useState({
    category: '',
    minEthicalScore: '',
    certification: '',
    riskLevel: ''
  });

  useEffect(() => {
    const unsubscribe = subscribe(setEthicalData);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'ethical_sourcing', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = async () => {
    setLoading(true);
    await refreshEthicalSourcingData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'ethical_sourcing_data', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setActiveTab('supplier-detail');
  };

  const handleExportReport = (supplierId = null) => {
    const reportData = generateEthicalSourcingReport(supplierId);
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ethical_sourcing_report_${supplierId || 'comprehensive'}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBulkExport = () => {
    const exportData = exportEthicalSourcingData('json', filterCriteria);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ethical_sourcing_bulk_export_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading || !ethicalData) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: '#e2e8f0', fontSize: '16px', fontWeight: '600' }}>
            Loading Ethical Sourcing System...
          </p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }}`}</style>
      </div>
    );
  }

  const overview = getEthicalSourcingOverview();
  const suppliers = getEthicalSuppliers();
  const standards = getEthicalStandards();
  const policies = getSourcingPolicies();
  const certifications = getCertificationPrograms();
  const riskAssessment = getRiskAssessment();
  const kpis = getMonitoringKPIs();
  const trends = calculateEthicalSourcingTrends();
  const highPerformers = getHighPerformingSuppliers();
  const needingImprovement = getSuppliersNeedingImprovement();

  // Filter suppliers
  const filteredSuppliers = suppliers.filter(supplier => {
    if (filterCriteria.category && supplier.category !== filterCriteria.category) return false;
    if (filterCriteria.minEthicalScore && supplier.ethicalScore < parseInt(filterCriteria.minEthicalScore)) return false;
    if (filterCriteria.certification && !supplier.certifications.some(cert => cert.name.includes(filterCriteria.certification))) return false;
    return true;
  });

  const OverviewTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
      {/* Ethical Sourcing Overview */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(124, 58, 237, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <span style={{ fontSize: '24px' }}>❤️</span>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Ethical Sourcing Overview
          </h3>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ 
            fontSize: '48px', 
            fontWeight: '900', 
            color: '#a855f7',
            textShadow: '0 0 20px rgba(168, 85, 247, 0.5)',
            marginBottom: '5px'
          }}>
            {overview.totalSuppliers}
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Ethical Suppliers
          </div>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ 
            fontSize: '32px', 
            fontWeight: '800', 
            color: '#22c55e',
            textShadow: '0 0 15px rgba(34, 197, 94, 0.4)'
          }}>
            {overview.averageEthicalScore}
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Average Ethical Score
          </div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ 
            height: '10px', 
            background: 'rgba(255, 255, 255, 0.1)', 
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${overview.ethicalSourcingPercentage}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
              borderRadius: '10px',
              boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
            }} />
          </div>
        </div>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#cbd5e1', textAlign: 'center' }}>
          {overview.ethicalSourcingPercentage}% Ethical Sourcing
        </div>
      </div>

      {/* Human Rights Compliance */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(59, 130, 246, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <span style={{ fontSize: '24px' }}>👥</span>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Human Rights Compliance
          </h3>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ 
            fontSize: '42px', 
            fontWeight: '900', 
            color: '#60a5fa',
            textShadow: '0 0 20px rgba(96, 165, 250, 0.5)'
          }}>
            {overview.humanRightsCompliance}%
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Overall Compliance
          </div>
        </div>
        {standards.humanRights.principles.slice(0, 3).map((principle, index) => (
          <div key={index} style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#e2e8f0' }}>
                {principle.name.split(' ')[0]}
              </span>
              <span style={{ fontSize: '13px', fontWeight: '800', color: '#cbd5e1' }}>
                {principle.compliance}%
              </span>
            </div>
            <div style={{ 
              height: '6px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '6px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${principle.compliance}%`,
                height: '100%',
                background: principle.compliance >= 95 
                  ? 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)'
                  : principle.compliance >= 85
                  ? 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)'
                  : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '6px'
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Conflict-Free Minerals */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(251, 191, 36, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <span style={{ fontSize: '24px' }}>💎</span>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Conflict-Free Sourcing
          </h3>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ 
            fontSize: '42px', 
            fontWeight: '900', 
            color: '#fbbf24',
            textShadow: '0 0 20px rgba(251, 191, 36, 0.5)'
          }}>
            {overview.conflictFreePercentage}%
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Conflict-Free Minerals
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#cbd5e1' }}>Smelter Audits</span>
          <span style={{ fontSize: '13px', fontWeight: '800', color: '#e2e8f0' }}>
            {policies.conflictMineralsPolicy.smelterAudits}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#cbd5e1' }}>Materials Covered</span>
          <span style={{ fontSize: '13px', fontWeight: '800', color: '#e2e8f0' }}>
            {policies.conflictMineralsPolicy.coverageMaterials.length}
          </span>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ 
            height: '8px', 
            background: 'rgba(255, 255, 255, 0.1)', 
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${overview.conflictFreePercentage}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
              borderRadius: '8px'
            }} />
          </div>
        </div>
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textAlign: 'center' }}>
          Target: 95% by 2025
        </div>
      </div>

      {/* Fair Trade Impact */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(34, 197, 94, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <HandshakeIcon size={24} />
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Fair Trade Impact
          </h3>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ 
            fontSize: '38px', 
            fontWeight: '900', 
            color: '#22c55e',
            textShadow: '0 0 20px rgba(34, 197, 94, 0.5)'
          }}>
            ${(policies.fairTradeCommitment.premiumsPaid / 1000000).toFixed(1)}M
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Premiums Paid
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#cbd5e1' }}>Producers Supported</span>
          <span style={{ fontSize: '13px', fontWeight: '800', color: '#e2e8f0' }}>
            {policies.fairTradeCommitment.producersSupported.toLocaleString()}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#cbd5e1' }}>Communities Impacted</span>
          <span style={{ fontSize: '13px', fontWeight: '800', color: '#e2e8f0' }}>
            {policies.fairTradeCommitment.communitiesImpacted}
          </span>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ 
            height: '8px', 
            background: 'rgba(255, 255, 255, 0.1)', 
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${policies.fairTradeCommitment.fairTradePercentage}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
              borderRadius: '8px',
              boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
            }} />
          </div>
        </div>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#cbd5e1', textAlign: 'center' }}>
          {policies.fairTradeCommitment.fairTradePercentage}% Fair Trade Spend
        </div>
      </div>


      {/* Ethical Standards Performance */}
      <div style={{
        gridColumn: '1 / -1',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '28px',
        border: '2px solid rgba(124, 58, 237, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        marginTop: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <span style={{ fontSize: '28px' }}>📊</span>
          <h3 style={{ fontSize: '22px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Ethical Standards Performance
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '2px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '16px'
          }}>
            <div style={{ 
              fontSize: '38px', 
              fontWeight: '900', 
              color: '#22c55e',
              marginBottom: '8px',
              textShadow: '0 0 15px rgba(34, 197, 94, 0.4)'
            }}>
              {standards.humanRights.overallCompliance}%
            </div>
            <div style={{ fontSize: '16px', fontWeight: '800', color: 'white', marginBottom: '6px' }}>
              Human Rights
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '12px' }}>
              ILO Conventions
            </div>
            <div style={{ 
              height: '8px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${standards.humanRights.overallCompliance}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                borderRadius: '8px'
              }} />
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '16px'
          }}>
            <div style={{ 
              fontSize: '38px', 
              fontWeight: '900', 
              color: '#60a5fa',
              marginBottom: '8px',
              textShadow: '0 0 15px rgba(96, 165, 250, 0.4)'
            }}>
              {standards.laborPractices.overallCompliance}%
            </div>
            <div style={{ fontSize: '16px', fontWeight: '800', color: 'white', marginBottom: '6px' }}>
              Labor Practices
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '12px' }}>
              Worker Satisfaction: {standards.laborPractices.workerSatisfaction}%
            </div>
            <div style={{ 
              height: '8px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${standards.laborPractices.overallCompliance}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)',
                borderRadius: '8px'
              }} />
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'rgba(251, 191, 36, 0.1)',
            border: '2px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '16px'
          }}>
            <div style={{ 
              fontSize: '38px', 
              fontWeight: '900', 
              color: '#fbbf24',
              marginBottom: '8px',
              textShadow: '0 0 15px rgba(251, 191, 36, 0.4)'
            }}>
              {standards.environmentalEthics.overallCompliance}%
            </div>
            <div style={{ fontSize: '16px', fontWeight: '800', color: 'white', marginBottom: '6px' }}>
              Environmental Ethics
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '12px' }}>
              Sustainability Score: {standards.environmentalEthics.sustainabilityScore}%
            </div>
            <div style={{ 
              height: '8px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${standards.environmentalEthics.overallCompliance}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
                borderRadius: '8px'
              }} />
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '24px',
            background: 'rgba(168, 85, 247, 0.1)',
            border: '2px solid rgba(168, 85, 247, 0.3)',
            borderRadius: '16px'
          }}>
            <div style={{ 
              fontSize: '38px', 
              fontWeight: '900', 
              color: '#a855f7',
              marginBottom: '8px',
              textShadow: '0 0 15px rgba(168, 85, 247, 0.4)'
            }}>
              {standards.businessEthics.overallCompliance}%
            </div>
            <div style={{ fontSize: '16px', fontWeight: '800', color: 'white', marginBottom: '6px' }}>
              Business Ethics
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '12px' }}>
              Training Completion: {standards.businessEthics.ethicsTrainingCompletion}%
            </div>
            <div style={{ 
              height: '8px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${standards.businessEthics.overallCompliance}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #a855f7 0%, #9333ea 100%)',
                borderRadius: '8px'
              }} />
            </div>
          </div>
        </div>
      </div>


      {/* High Performing Suppliers */}
      <div style={{
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
        gap: '20px',
        marginTop: '20px'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '24px',
          border: '2px solid rgba(34, 197, 94, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <span style={{ fontSize: '24px' }}>⭐</span>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Top Ethical Suppliers
            </h3>
          </div>
          {highPerformers.map((supplier, index) => (
            <div key={index} style={{
              marginBottom: '16px',
              padding: '20px',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <h4 style={{ fontSize: '16px', fontWeight: '800', color: 'white', margin: '0 0 6px 0' }}>
                    {supplier.name}
                  </h4>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>
                    {supplier.category} • {supplier.location}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '32px', 
                    fontWeight: '900', 
                    color: '#22c55e',
                    textShadow: '0 0 15px rgba(34, 197, 94, 0.5)',
                    lineHeight: '1'
                  }}>
                    {supplier.ethicalScore}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Ethical Score
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: '800', color: '#cbd5e1', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Certifications:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {supplier.certifications.slice(0, 3).map((cert, idx) => (
                    <span key={idx} style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: 'white',
                      background: '#22c55e',
                      borderRadius: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {cert.name}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#60a5fa', marginBottom: '4px' }}>
                    {supplier.ethicalMetrics.humanRights.score}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8' }}>
                    Human Rights
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#22c55e', marginBottom: '4px' }}>
                    {supplier.ethicalMetrics.laborPractices.score}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8' }}>
                    Labor
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#fbbf24', marginBottom: '4px' }}>
                    {supplier.ethicalMetrics.environmental.score}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8' }}>
                    Environment
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#a855f7', marginBottom: '4px' }}>
                    {supplier.ethicalMetrics.businessEthics.score}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8' }}>
                    Business
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Suppliers Needing Improvement */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '24px',
          border: '2px solid rgba(251, 191, 36, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Improvement Opportunities
            </h3>
          </div>
          {needingImprovement.map((supplier, index) => (
            <div key={index} style={{
              marginBottom: '16px',
              padding: '20px',
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <h4 style={{ fontSize: '16px', fontWeight: '800', color: 'white', margin: '0 0 6px 0' }}>
                    {supplier.name}
                  </h4>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>
                    {supplier.category} • {supplier.location}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '32px', 
                    fontWeight: '900', 
                    color: '#fbbf24',
                    textShadow: '0 0 15px rgba(251, 191, 36, 0.5)',
                    lineHeight: '1'
                  }}>
                    {supplier.ethicalScore}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Ethical Score
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: '800', color: '#ef4444', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Key Issues:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {supplier.improvementPlan?.slice(0, 2).map((action, idx) => (
                    <span key={idx} style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#000',
                      background: '#fbbf24',
                      borderRadius: '8px'
                    }}>
                      {action.area}
                    </span>
                  )) || <span style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>Assessment required</span>}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#cbd5e1' }}>
                  Improvement Progress
                </span>
                <span style={{ fontSize: '13px', fontWeight: '800', color: '#e2e8f0' }}>
                  {supplier.improvementPlan ? 
                    Math.round(supplier.improvementPlan.reduce((sum, a) => sum + a.progress, 0) / supplier.improvementPlan.length) 
                    : 0}%
                </span>
              </div>
              <div style={{ 
                height: '6px', 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${supplier.improvementPlan ? 
                    Math.round(supplier.improvementPlan.reduce((sum, a) => sum + a.progress, 0) / supplier.improvementPlan.length) 
                    : 0}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)',
                  borderRadius: '6px'
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const SuppliersTab = () => (
    <div>
      {/* Filter Controls */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(124, 58, 237, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <span style={{ fontSize: '24px' }}>🔍</span>
          <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Filter Ethical Suppliers
          </h3>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ 
              display: 'block',
              fontSize: '13px',
              fontWeight: '700',
              color: '#cbd5e1',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Category
            </label>
            <select 
              value={filterCriteria.category}
              onChange={(e) => setFilterCriteria({...filterCriteria, category: e.target.value})}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(124, 58, 237, 0.3)',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              <option value="" style={{ background: '#1e293b' }}>All Categories</option>
              <option value="Agriculture & Food" style={{ background: '#1e293b' }}>Agriculture & Food</option>
              <option value="Electronics & Technology" style={{ background: '#1e293b' }}>Electronics & Technology</option>
              <option value="Textiles & Apparel" style={{ background: '#1e293b' }}>Textiles & Apparel</option>
              <option value="Mining & Extraction" style={{ background: '#1e293b' }}>Mining & Extraction</option>
            </select>
          </div>
          
          <div>
            <label style={{ 
              display: 'block',
              fontSize: '13px',
              fontWeight: '700',
              color: '#cbd5e1',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Minimum Ethical Score
            </label>
            <select 
              value={filterCriteria.minEthicalScore}
              onChange={(e) => setFilterCriteria({...filterCriteria, minEthicalScore: e.target.value})}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(124, 58, 237, 0.3)',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              <option value="" style={{ background: '#1e293b' }}>No Minimum</option>
              <option value="90" style={{ background: '#1e293b' }}>90+ (Excellent)</option>
              <option value="80" style={{ background: '#1e293b' }}>80+ (Very Good)</option>
              <option value="70" style={{ background: '#1e293b' }}>70+ (Good)</option>
              <option value="60" style={{ background: '#1e293b' }}>60+ (Satisfactory)</option>
            </select>
          </div>
          
          <div>
            <label style={{ 
              display: 'block',
              fontSize: '13px',
              fontWeight: '700',
              color: '#cbd5e1',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Certification
            </label>
            <select 
              value={filterCriteria.certification}
              onChange={(e) => setFilterCriteria({...filterCriteria, certification: e.target.value})}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(124, 58, 237, 0.3)',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              <option value="" style={{ background: '#1e293b' }}>Any Certification</option>
              <option value="Fair Trade" style={{ background: '#1e293b' }}>Fair Trade</option>
              <option value="RBA" style={{ background: '#1e293b' }}>RBA Certified</option>
              <option value="B-Corp" style={{ background: '#1e293b' }}>B-Corp</option>
              <option value="SA8000" style={{ background: '#1e293b' }}>SA8000</option>
              <option value="Rainforest" style={{ background: '#1e293b' }}>Rainforest Alliance</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
            <button 
              onClick={() => setFilterCriteria({ category: '', minEthicalScore: '', certification: '', riskLevel: '' })}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '14px',
                fontWeight: '700',
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(148, 163, 184, 0.3)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              ❌ Clear
            </button>
            <button 
              onClick={handleBulkExport}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '14px',
                fontWeight: '700',
                color: 'white',
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                border: '2px solid rgba(124, 58, 237, 0.5)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 20px rgba(124, 58, 237, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              📥 Export
            </button>
          </div>
        </div>
        
        <div style={{
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: '700',
          color: 'white',
          background: '#60a5fa',
          borderRadius: '12px',
          display: 'inline-block',
          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
        }}>
          {filteredSuppliers.length} suppliers found
        </div>
      </div>


      {/* Suppliers Table */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(124, 58, 237, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <span style={{ fontSize: '24px' }}>🏢</span>
          <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Ethical Suppliers Directory
          </h3>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'left',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }}>Supplier</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'left',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }}>Category</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'left',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }}>Location</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }}>Score</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }} title="Human Rights">H</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }} title="Labor">L</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }} title="Environmental">E</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }} title="Business">B</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'left',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }}>Certifications</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'left',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }}>Last Assessment</th>
                <th style={{ 
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#cbd5e1',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((supplier, index) => (
                <tr key={index} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(124, 58, 237, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.01)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}>
                  <td style={{ padding: '16px', borderRadius: '12px 0 0 12px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '800', color: 'white' }}>
                      {supplier.name}
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>
                      {supplier.id}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: 'white',
                      background: '#64748b',
                      borderRadius: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {supplier.category}
                    </span>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600', color: '#cbd5e1' }}>
                    {supplier.location}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '6px 14px',
                      fontSize: '14px',
                      fontWeight: '800',
                      color: 'white',
                      background: supplier.ethicalScore >= 90 ? '#22c55e' : 
                                 supplier.ethicalScore >= 80 ? '#60a5fa' :
                                 supplier.ethicalScore >= 70 ? '#fbbf24' : '#ef4444',
                      borderRadius: '10px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {supplier.ethicalScore}
                    </span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 10px',
                      fontSize: '13px',
                      fontWeight: '800',
                      color: 'white',
                      background: supplier.ethicalMetrics.humanRights.score >= 90 ? '#22c55e' : 
                                 supplier.ethicalMetrics.humanRights.score >= 80 ? '#fbbf24' : '#ef4444',
                      borderRadius: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {supplier.ethicalMetrics.humanRights.score}
                    </span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 10px',
                      fontSize: '13px',
                      fontWeight: '800',
                      color: 'white',
                      background: supplier.ethicalMetrics.laborPractices.score >= 90 ? '#22c55e' : 
                                 supplier.ethicalMetrics.laborPractices.score >= 80 ? '#fbbf24' : '#ef4444',
                      borderRadius: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {supplier.ethicalMetrics.laborPractices.score}
                    </span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 10px',
                      fontSize: '13px',
                      fontWeight: '800',
                      color: 'white',
                      background: supplier.ethicalMetrics.environmental.score >= 90 ? '#22c55e' : 
                                 supplier.ethicalMetrics.environmental.score >= 80 ? '#fbbf24' : '#ef4444',
                      borderRadius: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {supplier.ethicalMetrics.environmental.score}
                    </span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 10px',
                      fontSize: '13px',
                      fontWeight: '800',
                      color: 'white',
                      background: supplier.ethicalMetrics.businessEthics.score >= 90 ? '#22c55e' : 
                                 supplier.ethicalMetrics.businessEthics.score >= 80 ? '#fbbf24' : '#ef4444',
                      borderRadius: '8px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {supplier.ethicalMetrics.businessEthics.score}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {supplier.certifications.slice(0, 2).map((cert, idx) => (
                        <span key={idx} style={{
                          padding: '3px 8px',
                          fontSize: '11px',
                          fontWeight: '700',
                          color: 'white',
                          background: '#22c55e',
                          borderRadius: '6px',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          whiteSpace: 'nowrap'
                        }}>
                          {cert.name}
                        </span>
                      ))}
                      {supplier.certifications.length > 2 && (
                        <span style={{
                          padding: '3px 8px',
                          fontSize: '11px',
                          fontWeight: '700',
                          color: 'white',
                          background: '#60a5fa',
                          borderRadius: '6px',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          +{supplier.certifications.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px', fontWeight: '600', color: '#cbd5e1' }}>
                    {new Date(supplier.lastAssessment).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center', borderRadius: '0 12px 12px 0' }}>
                    <button 
                      onClick={() => handleSupplierSelect(supplier)}
                      title="View Details"
                      style={{
                        padding: '8px 12px',
                        marginRight: '6px',
                        fontSize: '14px',
                        color: 'white',
                        background: 'rgba(96, 165, 250, 0.3)',
                        border: '2px solid #60a5fa',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#60a5fa';
                        e.target.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(96, 165, 250, 0.3)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      👁️
                    </button>
                    <button 
                      onClick={() => handleExportReport(supplier.id)}
                      title="Export Report"
                      style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        color: 'white',
                        background: 'rgba(148, 163, 184, 0.3)',
                        border: '2px solid #94a3b8',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#94a3b8';
                        e.target.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(148, 163, 184, 0.3)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      📥
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const StandardsTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px' }}>
      {/* Human Rights Standards */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(34, 197, 94, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{ fontSize: '24px' }}>👥</span>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Human Rights Standards
            </h3>
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Overall Compliance: {standards.humanRights.overallCompliance}%
          </div>
        </div>
        
        {standards.humanRights.principles.map((principle, index) => (
          <div key={index} style={{
            marginBottom: '16px',
            padding: '16px',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: 'white' }}>
                {principle.name}
              </span>
              <span style={{
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: '800',
                color: 'white',
                background: '#22c55e',
                borderRadius: '8px',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}>
                {principle.compliance}%
              </span>
            </div>
            <div style={{ 
              height: '8px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              <div style={{ 
                width: `${principle.compliance}%`,
                height: '100%',
                background: principle.compliance >= 95 
                  ? 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)'
                  : principle.compliance >= 90
                  ? 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)'
                  : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '8px'
              }} />
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>
              {principle.framework}
            </div>
          </div>
        ))}
      </div>

      {/* Labor Practices */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(96, 165, 250, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{ fontSize: '24px' }}>⚒️</span>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Labor Practices
            </h3>
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Worker Satisfaction: {standards.laborPractices.workerSatisfaction}%
          </div>
        </div>
        
        {standards.laborPractices.standards.map((standard, index) => (
          <div key={index} style={{
            marginBottom: '16px',
            padding: '16px',
            background: 'rgba(96, 165, 250, 0.1)',
            border: '1px solid rgba(96, 165, 250, 0.3)',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: 'white' }}>
                {standard.name}
              </span>
              <span style={{
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: '800',
                color: 'white',
                background: '#60a5fa',
                borderRadius: '8px',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}>
                {standard.compliance}%
              </span>
            </div>
            <div style={{ 
              height: '8px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              <div style={{ 
                width: `${standard.compliance}%`,
                height: '100%',
                background: standard.compliance >= 95 
                  ? 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)'
                  : standard.compliance >= 85
                  ? 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)'
                  : 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
                borderRadius: '8px'
              }} />
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>
              {standard.incidents !== undefined ? `Incidents: ${standard.incidents}` : 
               standard.programsConducted !== undefined ? `Programs: ${standard.programsConducted}` :
               standard.casesResolved !== undefined ? `Resolved: ${standard.casesResolved}/${standard.totalCases}` :
               standard.coveragePercentage !== undefined ? `Coverage: ${standard.coveragePercentage}%` :
               standard.facilitiesAudited !== undefined ? `Audited: ${standard.facilitiesAudited}` : ''}
            </div>
          </div>
        ))}
      </div>

      {/* Environmental Ethics */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(251, 191, 36, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{ fontSize: '24px' }}>🌿</span>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Environmental Ethics
            </h3>
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Sustainability Score: {standards.environmentalEthics.sustainabilityScore}%
          </div>
        </div>
        
        {standards.environmentalEthics.practices.map((practice, index) => (
          <div key={index} style={{
            marginBottom: '16px',
            padding: '16px',
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: 'white' }}>
                {practice.name}
              </span>
              <span style={{
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: '800',
                color: '#000',
                background: '#fbbf24',
                borderRadius: '8px'
              }}>
                {practice.compliance}%
              </span>
            </div>
            <div style={{ 
              height: '8px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              <div style={{ 
                width: `${practice.compliance}%`,
                height: '100%',
                background: practice.compliance >= 85 
                  ? 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)'
                  : practice.compliance >= 75
                  ? 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)'
                  : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '8px'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>
              <span>
                Current: {practice.percentage || practice.reduction || practice.recyclingRate || practice.protectedAreas || practice.efficiency}
                {practice.percentage !== undefined || practice.reduction !== undefined || 
                 practice.recyclingRate !== undefined || practice.efficiency !== undefined ? '%' : ''}
              </span>
              <span>
                Target: {practice.target}{practice.target > 100 ? '' : '%'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Business Ethics */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(168, 85, 247, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{ fontSize: '24px' }}>⚖️</span>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Business Ethics
            </h3>
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#a855f7', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Training Completion: {standards.businessEthics.ethicsTrainingCompletion}%
          </div>
        </div>
        
        {standards.businessEthics.principles.map((principle, index) => (
          <div key={index} style={{
            marginBottom: '16px',
            padding: '16px',
            background: 'rgba(168, 85, 247, 0.1)',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: 'white' }}>
                {principle.name}
              </span>
              <span style={{
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: '800',
                color: 'white',
                background: '#a855f7',
                borderRadius: '8px',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}>
                {principle.compliance}%
              </span>
            </div>
            <div style={{ 
              height: '8px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              <div style={{ 
                width: `${principle.compliance}%`,
                height: '100%',
                background: principle.compliance >= 95 
                  ? 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)'
                  : principle.compliance >= 90
                  ? 'linear-gradient(90deg, #a855f7 0%, #9333ea 100%)'
                  : 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
                borderRadius: '8px'
              }} />
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>
              {principle.violationsReported !== undefined ? `Violations: ${principle.violationsReported}` :
               principle.competitiveAudits !== undefined ? `Audits: ${principle.competitiveAudits}/${principle.passed}` :
               principle.disclosureLevel !== undefined ? `Disclosure: ${principle.disclosureLevel}%` :
               principle.conflictFreePercentage !== undefined ? `Conflict-Free: ${principle.conflictFreePercentage}%` :
               principle.gdprCompliance !== undefined ? `GDPR: ${principle.gdprCompliance}%` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const CertificationsTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px' }}>
      {certifications.map((cert, index) => (
        <div key={index} style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '24px',
          border: '2px solid rgba(34, 197, 94, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 40px rgba(34, 197, 94, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
        }}>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>🏆</span>
                <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                  {cert.name}
                </h3>
              </div>
              <span style={{
                padding: '6px 14px',
                fontSize: '14px',
                fontWeight: '800',
                color: 'white',
                background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                borderRadius: '10px',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)'
              }}>
                {cert.coverage}%
              </span>
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Coverage of suppliers
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: '800', 
              color: '#cbd5e1', 
              textTransform: 'uppercase', 
              letterSpacing: '1px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>📋</span>
              Standards Covered:
            </div>
            {cert.standards.map((standard, idx) => (
              <div key={idx} style={{ 
                fontSize: '13px', 
                fontWeight: '600', 
                color: '#cbd5e1',
                marginBottom: '6px',
                paddingLeft: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ 
                  width: '6px', 
                  height: '6px', 
                  borderRadius: '50%', 
                  background: '#22c55e',
                  boxShadow: '0 0 6px rgba(34, 197, 94, 0.6)'
                }} />
                {standard}
              </div>
            ))}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: '800', 
              color: '#cbd5e1', 
              textTransform: 'uppercase', 
              letterSpacing: '1px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>🏢</span>
              Certification Providers:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {cert.providers.map((provider, idx) => (
                <span key={idx} style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'white',
                  background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                  borderRadius: '8px',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                }}>
                  {provider}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              padding: '16px',
              background: 'rgba(96, 165, 250, 0.1)',
              border: '2px solid rgba(96, 165, 250, 0.3)',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#60a5fa', marginBottom: '6px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                {cert.auditFrequency}
              </div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Audit Frequency
              </div>
            </div>
            <div style={{
              padding: '16px',
              background: 'rgba(168, 85, 247, 0.1)',
              border: '2px solid rgba(168, 85, 247, 0.3)',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#a855f7', marginBottom: '6px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                {cert.cost}
              </div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Cost Structure
              </div>
            </div>
          </div>

          <div>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: '700', 
              color: '#cbd5e1',
              marginBottom: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ textTransform: 'uppercase', letterSpacing: '1px' }}>Supplier Coverage</span>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#22c55e' }}>{cert.coverage}%</span>
            </div>
            <div style={{ 
              height: '10px', 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)'
            }}>
              <div style={{ 
                width: `${cert.coverage}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                borderRadius: '10px',
                boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)',
                transition: 'width 0.5s ease'
              }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }}>
      {/* Hero Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: '60px 40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <svg style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.1
        }}>
          <defs>
            <pattern id="ethical-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 10 60 M 0 10 L 60 10 M 30 0 L 30 60 M 0 30 L 60 30 M 50 0 L 50 60 M 0 50 L 60 50" 
                    stroke="white" strokeWidth="1" fill="none"/>
            </pattern>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#ethical-pattern)" />
        </svg>
        
        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '36px'
            }}>
              ❤️
            </div>
            <div>
              <h1 style={{ 
                fontSize: '42px', 
                fontWeight: '900', 
                color: 'white',
                margin: '0 0 10px 0',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}>
                Ethical Sourcing
              </h1>
              <p style={{ 
                fontSize: '18px', 
                color: 'rgba(255, 255, 255, 0.95)',
                margin: 0,
                fontWeight: '500'
              }}>
                Comprehensive ethical procurement and human rights compliance
              </p>
            </div>
          </div>
          
          <button 
            onClick={handleRefreshData}
            disabled={loading}
            style={{
              padding: '14px 28px',
              fontSize: '15px',
              fontWeight: '700',
              color: 'white',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.3)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.15)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(124, 58, 237, 0.3)',
        padding: '20px 40px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '15px' }}>
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'suppliers', label: 'Ethical Suppliers', icon: '🏢' },
            { id: 'standards', label: 'Standards', icon: '📋' },
            { id: 'certifications', label: 'Certifications', icon: '🏆' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: '700',
                color: activeTab === tab.id ? 'white' : '#cbd5e1',
                background: activeTab === tab.id 
                  ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
                  : 'transparent',
                border: activeTab === tab.id 
                  ? '2px solid rgba(124, 58, 237, 0.5)'
                  : '2px solid rgba(124, 58, 237, 0.2)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'rgba(124, 58, 237, 0.2)';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#cbd5e1';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px' }}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'suppliers' && <SuppliersTab />}
        {activeTab === 'standards' && <StandardsTab />}
        {activeTab === 'certifications' && <CertificationsTab />}
      </div>
    </div>
  );
};

export default EthicalSourcing;